import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import DOMPurify from 'dompurify';
import './PrivacyPolicyGenerator.css';

const PrivacyPolicyGenerator = ({ domain, siteId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    companyName: '',
    contactEmail: '',
    contactAddress: '',
    dataTypes: ['name', 'email', 'browser information'],
    dataUsage: ['provide services', 'improve user experience'],
    thirdParties: ['analytics providers', 'advertising partners'],
    cookiesUsed: true,
    gdprApplicable: false,
    ccpaApplicable: false,
    dataRetentionPeriod: 'as long as necessary',
    securityMeasures: ['encryption', 'secure servers'],
    childrenPolicy: 'not intended for children under 13',
    internationalTransfers: false,
    policyUpdateMethod: 'posting the new Privacy Policy on this page',
  });

  useEffect(() => {
    loadAnalysis();
  }, [domain]); // eslint-disable-line react-hooks/exhaustive-deps -- reload when domain changes; siteId from props

  const loadAnalysis = async () => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      params.append('domain', domain);
      if (siteId) {
        params.append('siteId', siteId);
      }

      const response = await axios.get(`/api/compliance/privacy-policy?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const analysisData = response.data.analysis;
      setAnalysis({
        ...analysisData,
        hasPolicy: response.data.hasPrivacyPolicy || analysisData.hasPolicy,
      });
      
      // Pre-fill form with domain if no company name
      if (!analysisData?.elements?.companyInfo?.found) {
        setFormData(prev => ({
          ...prev,
          companyName: domain,
        }));
      }
      
      // Pre-fill with user email if available
      try {
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        const userEmail = userData.email || '';
        if (userEmail && !analysisData?.elements?.contactInfo?.found) {
          setFormData(prev => ({
            ...prev,
            contactEmail: userEmail,
          }));
        }
      } catch (e) {
        // Ignore
      }
    } catch (err) {
      console.error('Error loading privacy policy analysis:', err);
      setError(err.response?.data?.message || 'Failed to load privacy policy analysis. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setResult(null);
    setCopied(false);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `/api/compliance/privacy-policy`,
        {
          domain,
          siteId,
          privacyPolicyText: analysis?.privacyPolicyText || analysis?.text || null,
          data: formData,
          generateNew: !(analysis?.hasPolicy || analysis?.hasPrivacyPolicy),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult(response.data);
    } catch (err) {
      console.error('Error generating optimized privacy policy:', err);
      setError(err.response?.data?.message || 'Failed to generate optimized privacy policy. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    if (result?.optimizedPrivacyPolicy) {
      navigator.clipboard.writeText(result.optimizedPrivacyPolicy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    if (result?.optimizedPrivacyPolicy) {
      const blob = new Blob([result.optimizedPrivacyPolicy], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'privacy-policy.html';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const updateFormData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const addArrayItem = (field, value) => {
    if (value.trim()) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()],
      }));
    }
  };

  const removeArrayItem = (field, index) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const renderElementStatus = () => {
    if (!analysis) return null;

    const elements = analysis.elements || {};
    const found = analysis.found || [];

    return (
      <div className="privacy-policy-elements">
        <h3>Privacy Policy Elements</h3>
        <div className="elements-grid">
          {Object.entries(elements).map(([key, element]) => {
            const isFound = found.includes(key);
            const isRequired = element.required;
            
            return (
              <div
                key={key}
                className={`element-item ${isFound ? 'found' : 'missing'} ${isRequired ? 'required' : ''}`}
              >
                <div className="element-header">
                  <span className="element-status">
                    {isFound ? '✓' : '✗'}
                  </span>
                  <span className="element-name">{element.name}</span>
                  {isRequired && <span className="required-badge">Required</span>}
                </div>
                <p className="element-description">{element.description}</p>
              </div>
            );
          })}
        </div>
        
        {analysis.score !== undefined && (
          <div className="analysis-score">
            <p>
              <strong>Completeness Score:</strong> {analysis.score}%
              {analysis.requiredScore !== undefined && (
                <span className="required-score"> (Required elements: {analysis.requiredScore}%)</span>
              )}
            </p>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="privacy-policy-generator-modal">
        <div className="privacy-policy-generator-content">
          <div className="privacy-policy-generator-loading">
            <div className="spinner" />
            <p>Analyzing privacy policy...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="privacy-policy-generator-modal">
      <div className="privacy-policy-generator-content">
        <div className="privacy-policy-generator-header">
          <h2>Generate Optimized Privacy Policy</h2>
          <button className="privacy-policy-generator-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="privacy-policy-generator-body">
          {error && (
            <div className="privacy-policy-generator-error">
              <p>{error}</p>
            </div>
          )}

          {!result && (
            <>
              {analysis && (
                <>
                  {!(analysis.hasPolicy || analysis.hasPrivacyPolicy) && (
                    <div className="status-banner info">
                      <p>No privacy policy detected. Please fill in the information below to generate a complete privacy policy.</p>
                    </div>
                  )}

                  {(analysis.hasPolicy || analysis.hasPrivacyPolicy) && analysis.missing && analysis.missing.length > 0 && (
                    <div className="status-banner warning">
                      <p>
                        Privacy policy detected but missing {analysis.missing.length} element{analysis.missing.length > 1 ? 's' : ''}.
                        Fill in the missing information below to generate an optimized version.
                      </p>
                    </div>
                  )}

                  {(analysis.hasPolicy || analysis.hasPrivacyPolicy) && (!analysis.missing || analysis.missing.length === 0) && (
                    <div className="status-banner success">
                      <p>Privacy policy detected with all required elements. You can update the information below and generate a new version.</p>
                    </div>
                  )}

                  {renderElementStatus()}
                </>
              )}

              <div className="privacy-policy-form">
                <h3>Privacy Policy Information</h3>

                <div className="form-section">
                  <label>
                    <span>Company/Website Name *</span>
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => updateFormData('companyName', e.target.value)}
                      placeholder={domain}
                      required
                    />
                  </label>
                </div>

                <div className="form-section">
                  <label>
                    <span>Contact Email *</span>
                    <input
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => updateFormData('contactEmail', e.target.value)}
                      placeholder="privacy@example.com"
                      required
                    />
                  </label>
                </div>

                <div className="form-section">
                  <label>
                    <span>Contact Address</span>
                    <input
                      type="text"
                      value={formData.contactAddress}
                      onChange={(e) => updateFormData('contactAddress', e.target.value)}
                      placeholder="123 Main St, City, State, ZIP"
                    />
                  </label>
                </div>

                <div className="form-section">
                  <label>
                    <span>Types of Data Collected *</span>
                    <div className="array-input">
                      {formData.dataTypes.map((item, index) => (
                        <div key={index} className="array-item">
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => {
                              const newArray = [...formData.dataTypes];
                              newArray[index] = e.target.value;
                              updateFormData('dataTypes', newArray);
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => removeArrayItem('dataTypes', index)}
                            className="remove-item"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addArrayItem('dataTypes', '')}
                        className="add-item"
                      >
                        + Add Item
                      </button>
                    </div>
                  </label>
                </div>

                <div className="form-section">
                  <label>
                    <span>How Data is Used *</span>
                    <div className="array-input">
                      {formData.dataUsage.map((item, index) => (
                        <div key={index} className="array-item">
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => {
                              const newArray = [...formData.dataUsage];
                              newArray[index] = e.target.value;
                              updateFormData('dataUsage', newArray);
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => removeArrayItem('dataUsage', index)}
                            className="remove-item"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addArrayItem('dataUsage')}
                        className="add-item"
                      >
                        + Add Item
                      </button>
                    </div>
                  </label>
                </div>

                <div className="form-section">
                  <label>
                    <span>Third Parties</span>
                    <div className="array-input">
                      {formData.thirdParties.map((item, index) => (
                        <div key={index} className="array-item">
                          <input
                            type="text"
                            value={item}
                            onChange={(e) => {
                              const newArray = [...formData.thirdParties];
                              newArray[index] = e.target.value;
                              updateFormData('thirdParties', newArray);
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => removeArrayItem('thirdParties', index)}
                            className="remove-item"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addArrayItem('thirdParties')}
                        className="add-item"
                      >
                        + Add Item
                      </button>
                    </div>
                  </label>
                </div>

                <div className="form-section">
                  <label>
                    <span>Data Retention Period</span>
                    <input
                      type="text"
                      value={formData.dataRetentionPeriod}
                      onChange={(e) => updateFormData('dataRetentionPeriod', e.target.value)}
                      placeholder="as long as necessary"
                    />
                  </label>
                </div>

                <div className="form-section checkbox-section">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.cookiesUsed}
                      onChange={(e) => updateFormData('cookiesUsed', e.target.checked)}
                    />
                    <span>We use cookies and tracking technologies</span>
                  </label>
                </div>

                <div className="form-section checkbox-section">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.gdprApplicable}
                      onChange={(e) => updateFormData('gdprApplicable', e.target.checked)}
                    />
                    <span>GDPR applies (EU users)</span>
                  </label>
                </div>

                <div className="form-section checkbox-section">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.ccpaApplicable}
                      onChange={(e) => updateFormData('ccpaApplicable', e.target.checked)}
                    />
                    <span>CCPA applies (California users)</span>
                  </label>
                </div>

                <div className="form-section checkbox-section">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.internationalTransfers}
                      onChange={(e) => updateFormData('internationalTransfers', e.target.checked)}
                    />
                    <span>We transfer data internationally</span>
                  </label>
                </div>

                <div className="privacy-policy-generator-actions">
                  <button
                    className="btn-generate"
                    onClick={handleGenerate}
                    disabled={generating || !formData.companyName || !formData.contactEmail}
                  >
                    {generating ? 'Generating...' : 'Generate Optimized Privacy Policy'}
                  </button>
                </div>
              </div>
            </>
          )}

          {result && (
            <div className="privacy-policy-generator-result">
              <div className="result-summary">
                <h3>Privacy Policy Generated</h3>
                <p>
                  {result.isNew
                    ? 'A new privacy policy has been generated with all required elements.'
                    : 'Your privacy policy has been optimized with missing elements added.'}
                </p>
                {result.changes && result.changes.length > 0 && (
                  <div className="changes-list">
                    <h4>Changes Made:</h4>
                    <ul>
                      {result.changes.map((change, index) => (
                        <li key={index}>
                          {change.element}: {change.action}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="privacy-policy-generator-output">
                <div className="output-header">
                  <h4>Optimized Privacy Policy</h4>
                  <div className="output-actions">
                    <button
                      className="btn-copy"
                      onClick={handleCopy}
                      disabled={copied}
                    >
                      {copied ? 'Copied!' : 'Copy HTML'}
                    </button>
                    <button className="btn-download" onClick={handleDownload}>
                      Download
                    </button>
                  </div>
                </div>
                <div
                  className="privacy-policy-preview"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(result.optimizedPrivacyPolicy) }}
                />
                <textarea
                  readOnly
                  value={result.optimizedPrivacyPolicy}
                  className="privacy-policy-output"
                  rows={30}
                />
              </div>

              <div className="privacy-policy-generator-instructions">
                <h4>Next Steps</h4>
                <ol>
                  <li>Review the generated privacy policy above</li>
                  <li>Customize any sections as needed for your specific business</li>
                  <li>Copy or download the HTML file</li>
                  <li>Upload it to your server and link to it from your website footer/navigation</li>
                  <li>Ensure it's accessible at a URL like <code>https://{domain}/privacy-policy</code></li>
                </ol>
              </div>
            </div>
          )}
        </div>

        <div className="privacy-policy-generator-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
          {result && (
            <button className="btn-primary" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy to Clipboard'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

PrivacyPolicyGenerator.propTypes = {
  domain: PropTypes.string.isRequired,
  siteId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default PrivacyPolicyGenerator;

